import React, { useState } from "react";

import { createSelector } from "@reduxjs/toolkit";
import { INSURANCE_TYPE_OPTIONS } from "BrokerOfRecord/constants";
import { useUpdateUserExperienceFlagsMutation } from "EmployerApplication/api";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { formValueSelector, reduxForm, SubmissionError, resetSection } from "redux-form";
import focusFirstError from "Utils/focusFirstError";
import { v4 as uuidv4 } from "uuid";

import { useUpdateApplicationMutation, getBorApplication, useGetApplicationQuery } from "../api";
import BORPage from "../components/BORPage";
import {
  PlanName,
  MedicalInsuranceProvider,
  OtherMedicalInsuranceProvider,
  DentalInsuranceProvider,
  OtherDentalInsuranceProvider,
  VisionInsuranceProvider,
  OtherVisionInsuranceProvider,
  MedicalGroupIdNumber,
  DentalGroupIdNumber,
  VisionGroupIdNumber,
  LifeInsuranceProvider,
  OtherLifeInsuranceProvider,
  LifeGroupIdNumber,
  DisabilityInsuranceProvider,
  OtherDisabilityInsuranceProvider,
  DisabilityGroupIdNumber,
} from "../components/CommonQuestions";
import { baseURL, companyInformationURL, confirmationURL } from "../urls";
import { H4, Title, Subtitle } from "Cobalt/Header";
import AppearingElement from "Common/AppearingElement";
import Button from "Common/Button";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import RemoveIcon from "Common/RemoveIcon";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const CURRENT_PLAN_FORM = "currentPlan";

const withReduxForm = reduxForm({
  form: CURRENT_PLAN_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true,
  onSubmit: async (values, _dispatch, props) => {
    const extractPlans = (prefix: string) => {
      const regex = new RegExp(`^${prefix}(.*)_(\\d+)$`);
      const plans: Record<number, any> = {};

      Object.entries(values).forEach(([key, val]) => {
        const match = key.match(regex);
        if (!match) return;

        const field = match[1];
        const idx = parseInt(match[2], 10);

        if (!plans[idx]) {
          plans[idx] = { id: uuidv4().split("-")[0] };
        }

        plans[idx][field.charAt(0).toLowerCase() + field.slice(1)] = val;
      });

      return Object.values(plans);
    };

    const payload = {
      borApplicationIntake: {
        plans: {
          medical: extractPlans("borMedical"),
          dental: extractPlans("borDental"),
          vision: extractPlans("borVision"),
          termLife: extractPlans("borTermLife"),
          wholeLife: extractPlans("borWholeLife"),
          shortTermDisability: extractPlans("borShortTermDisability"),
          longTermDisability: extractPlans("borLongTermDisability"),
        },
      },
    };
    try {
      await props.updateBorApplication(payload).unwrap();
    } catch (e) {
      const {
        data: { error, fieldErrors },
      } = e as any;
      throw new SubmissionError({
        _error: error,
        ...fieldErrors,
      });
    }
  },
  onSubmitFail: (errors) => {
    focusFirstError(errors, ".");
  },
  onSubmitSuccess: (_values, _dispatch, { history }) => {
    history.push(`${baseURL}${confirmationURL}`);
  },
});

// Map insurance type → carrier + group components
const insuranceFieldComponents: Record<
  string,
  {
    Carrier: React.ComponentType<any>;
    OtherCarrier?: React.ComponentType<any>;
    GroupNumber: React.ComponentType<any>;
  }
> = {
  dental: {
    Carrier: DentalInsuranceProvider,
    OtherCarrier: OtherDentalInsuranceProvider,
    GroupNumber: DentalGroupIdNumber,
  },
  vision: {
    Carrier: VisionInsuranceProvider,
    OtherCarrier: OtherVisionInsuranceProvider,
    GroupNumber: VisionGroupIdNumber,
  },
  termLife: {
    Carrier: LifeInsuranceProvider,
    OtherCarrier: OtherLifeInsuranceProvider,
    GroupNumber: LifeGroupIdNumber,
  },
  wholeLife: {
    Carrier: LifeInsuranceProvider,
    OtherCarrier: OtherLifeInsuranceProvider,
    GroupNumber: LifeGroupIdNumber,
  },
  shortTermDisability: {
    Carrier: DisabilityInsuranceProvider,
    OtherCarrier: OtherDisabilityInsuranceProvider,
    GroupNumber: DisabilityGroupIdNumber,
  },
  longTermDisability: {
    Carrier: DisabilityInsuranceProvider,
    OtherCarrier: OtherDisabilityInsuranceProvider,
    GroupNumber: DisabilityGroupIdNumber,
  },
};

const capitalize = (str: string) => {
  if (str.length === 0) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const CurrentPlanForm = withReduxForm(({ handleSubmit, submitting, initialValues }) => {
  const dispatch = useDispatch();
  const formSelector = formValueSelector(CURRENT_PLAN_FORM);
  const stateCode = useSelector((state) => formSelector(state, "state"));

  // helper: figure out how many plan indexes exist in initialValues for a prefix
  const extractIndexes = (prefix: string) => {
    const regex = new RegExp(`^${prefix}PlanName_(\\d+)$`);
    return Object.keys(initialValues || {})
      .map((k) => {
        const m = k.match(regex);
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n) => n !== null) as number[];
  };

  const [plansByType, setPlansByType] = useState<{ [key: string]: number[] }>(() => {
    const init: { [key: string]: number[] } = {};
    const medicalIdxs = extractIndexes("borMedical").sort((a, b) => a - b);
    init.medical = medicalIdxs.length ? medicalIdxs : [0];

    INSURANCE_TYPE_OPTIONS.forEach(({ value }) => {
      const cap = value.charAt(0).toUpperCase() + value.slice(1);
      const indexes = extractIndexes(`bor${cap}`).sort((a, b) => a - b);
      if (indexes.length) init[value] = indexes;
    });
    return init;
  });

  // Build a list of ALL carrier field names currently on the page (one hook read for all values)
  const allCarrierFieldNames: string[] = React.useMemo(() => {
    const names: string[] = [];

    // medical
    (plansByType.medical || []).forEach((idx) => {
      names.push(`borMedicalInsuranceCompany_${idx}`);
    });

    // other types
    Object.entries(plansByType).forEach(([type, planIndexes]) => {
      if (type === "medical") return;
      const cap = capitalize(type);
      (planIndexes || []).forEach((idx) => {
        names.push(`bor${cap}InsuranceCompany_${idx}`);
      });
    });

    return names;
  }, [plansByType]);

  // Read all carrier values at once; normalize single-field case to object shape
  const carrierValuesRaw = useSelector((state) => formSelector(state, ...allCarrierFieldNames));
  const carrierValues: Record<string, any> = React.useMemo(() => {
    if (allCarrierFieldNames.length === 1) {
      return { [allCarrierFieldNames[0]]: carrierValuesRaw };
    }
    return (carrierValuesRaw as Record<string, any>) || {};
  }, [carrierValuesRaw, allCarrierFieldNames]);

  // Tolerant "isOther" check for strings or { value/label }
  const isOther = (v: any) => {
    if (v == null) return false;
    if (typeof v === "string") return v.trim().toLowerCase() === "other";
    if (typeof v === "object") {
      if ("value" in v && v.value != null) return String(v.value).trim().toLowerCase() === "other";
      if ("label" in v && v.label != null) return String(v.label).trim().toLowerCase() === "other";
    }
    return false;
  };

  // Add a new plan for a given type
  const addPlan = (type: string) => {
    setPlansByType((prev) => {
      const nextIndex = prev[type]?.length ?? 0;
      return {
        ...prev,
        [type]: [...(prev[type] || []), nextIndex],
      };
    });
  };

  // Helper to remove a specific row from a section
  const removePlan = (type: string, idx: number) => {
    setPlansByType((prev) => {
      const newPlans = { ...prev };
      newPlans[type] = (newPlans[type] || []).filter((i) => i !== idx);
      if (newPlans[type].length === 0) {
        delete newPlans[type];
      }
      return newPlans;
    });
  };

  // Add a new insurance type section
  const addInsuranceType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected && !plansByType[selected]) {
      setPlansByType((prev) => ({ ...prev, [selected]: [0] }));
    }
    e.target.value = ""; // reset dropdown
  };

  return (
    <form className={bem("form")} onSubmit={handleSubmit}>
      {Object.entries(plansByType).map(([type, planIndexes]) => {
        // Special-case Medical
        if (type === "medical") {
          return (
            <div key="medical" className={bem("form", "insuranceSection")}>
              <Row className={bem("form", "titleSection")}>
                <Col>
                  <Title className={bem("form", "title")}>Medical</Title>
                </Col>
              </Row>

              {(planIndexes || []).map((idx) => {
                const planName = `borMedicalPlanName_${idx}`;
                const carrierName = `borMedicalInsuranceCompany_${idx}`;
                const otherCarrierName = `borMedicalOtherInsuranceCompany_${idx}`;
                const groupName = `borMedicalGroupIDNumber_${idx}`;

                const carrierVal = carrierValues[carrierName];

                return (
                  <Row className={`BORPlanRow-${type}`} key={idx}>
                    <Col sm={3} className={bem("form", "col")}>
                      <PlanName name={planName} className={bem("form", "inputField")} />
                    </Col>
                    <Col sm={4} className={bem("form", "col")}>
                      <MedicalInsuranceProvider name={carrierName} label="Plan Carrier" onChange={() => dispatch(resetSection(CURRENT_PLAN_FORM, groupName))} />

                      <AppearingElement show={isOther(carrierVal)} stayShown>
                        <OtherMedicalInsuranceProvider className="px-0" name={otherCarrierName} label="Other Carrier" />
                      </AppearingElement>
                    </Col>
                    <Col sm={4} className={bem("form", "col")}>
                      <MedicalGroupIdNumber name={groupName} label="Group Number" stateCode={stateCode} className={bem("form", "inputField")} />
                    </Col>
                    {idx !== 0 && (
                      <Col sm={1} className="text-right">
                        <Button className="pt-8" type="button" size="small" color="link" onClick={() => removePlan(type, idx)}>
                          <RemoveIcon />
                        </Button>
                      </Col>
                    )}
                  </Row>
                );
              })}

              <Row className="pb-4">
                <Col className="text-left">
                  <Button type="button" size="small" color="link" onClick={() => addPlan("medical")}>
                    + Add another Medical plan
                  </Button>
                </Col>
              </Row>
            </div>
          );
        }

        // All other types (Dental, Vision, Life, Disability…)
        const fields = insuranceFieldComponents[type];
        if (!fields) return null; // safeguard

        const title = INSURANCE_TYPE_OPTIONS.find((opt) => opt.value === type)?.label;

        return (
          <div key={type} className={bem("form", "insuranceSection")}>
            <Row className={bem("form", "titleSection")}>
              <Col>
                <Title className={bem("form", "title")}>{title}</Title>
              </Col>
            </Row>

            {(planIndexes || []).map((idx) => {
              const capitalizedType = capitalize(type);
              const planName = `bor${capitalizedType}PlanName_${idx}`;
              const carrierName = `bor${capitalizedType}InsuranceCompany_${idx}`;
              const otherCarrierName = `bor${capitalizedType}OtherInsuranceCompany_${idx}`;
              const groupName = `bor${capitalizedType}GroupIDNumber_${idx}`;

              const carrierVal = carrierValues[carrierName];

              return (
                <Row className={`BORPlanRow-${type}`} key={idx}>
                  <Col sm={3} className={bem("form", "col")}>
                    <PlanName name={planName} className={bem("form", "inputField")} />
                  </Col>
                  <Col sm={4} className={bem("form", "col")}>
                    <fields.Carrier name={carrierName} label="Plan Carrier" onChange={() => dispatch(resetSection(CURRENT_PLAN_FORM, groupName))} />

                    <AppearingElement show={isOther(carrierVal)} stayShown>
                      {fields.OtherCarrier && <fields.OtherCarrier className="px-0" name={otherCarrierName} label="Other Carrier" />}
                    </AppearingElement>
                  </Col>
                  <Col sm={4} className={bem("form", "col")}>
                    <fields.GroupNumber name={groupName} label="Group Number" stateCode={stateCode} className={bem("form", "inputField")} />
                  </Col>
                  <Col sm={1} className="text-right">
                    <Button className="pt-8" type="button" size="small" color="link" onClick={() => removePlan(type, idx)}>
                      <RemoveIcon />
                    </Button>
                  </Col>
                </Row>
              );
            })}

            <Row className="pb-4">
              <Col className="text-left">
                <Button type="button" size="small" color="link" onClick={() => addPlan(type)}>
                  + Add another {title} plan
                </Button>
              </Col>
            </Row>
          </div>
        );
      })}

      {/* Dropdown to add new insurance types */}
      <Row className={bem("form", "titleSection")}>
        <Col>
          <Title className={bem("form", "title")}>Add an insurance type</Title>
        </Col>
      </Row>
      <Row className={bem("form", "addInsuranceTypeRow")}>
        <Col sm={6}>
          <select defaultValue="" onChange={addInsuranceType}>
            <option value="" disabled>
              Select type…
            </option>
            {INSURANCE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={!!plansByType[opt.value]}>
                {opt.label}
              </option>
            ))}
          </select>
        </Col>
      </Row>

      <Row>
        <Col smOffset={8} mdOffset={9}>
          <Button fullWidth submitting={submitting} disabled={submitting} size="medium" type="submit">
            Next
          </Button>
        </Col>
      </Row>
    </form>
  );
});

const formInitialValues = createSelector(getBorApplication, (borApplication) => {
  if (!borApplication) return {};

  const mapPlans = (plans: any[], prefix: string) => {
    const out: Record<string, any> = {};
    plans?.forEach((plan, idx) => {
      out[`${prefix}Id_${idx}`] = plan.id || uuidv4().split("-")[0];
      out[`${prefix}PlanName_${idx}`] = plan.planName;
      out[`${prefix}InsuranceCompany_${idx}`] = plan.insuranceCompany;
      out[`${prefix}OtherInsuranceCompany_${idx}`] = plan.otherInsuranceCompany;
      out[`${prefix}GroupIDNumber_${idx}`] = plan.groupIDNumber;
    });
    return out;
  };

  return {
    ...mapPlans(borApplication.borApplicationIntake?.plans?.medical, "borMedical"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.dental, "borDental"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.vision, "borVision"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.termLife, "borTermLife"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.wholeLife, "borWholeLife"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.shortTermDisability, "borShortTermDisability"),
    ...mapPlans(borApplication.borApplicationIntake?.plans?.longTermDisability, "borLongTermDisability"),
  };
});

const CurrentPlanPage = () => {
  const [updateBorApplication] = useUpdateApplicationMutation();
  const [updateUserExperienceFlags] = useUpdateUserExperienceFlagsMutation();
  const { data: borApplication, isLoading: _isLoadingBorApplication, error: _borError } = useGetApplicationQuery();

  const history = useHistory();
  const initialValues = useSelector(formInitialValues);

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${companyInformationURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>
      <H4 className={bem("title")}>Insurance plans</H4>
      <Subtitle className={bem("subtitle")}>Tell us about your current plans.</Subtitle>
      <hr className={bem("divider")} />
      <CurrentPlanForm
        updateBorApplication={updateBorApplication}
        updateUserExperienceFlags={updateUserExperienceFlags}
        idToken={borApplication?.idToken}
        history={history}
        initialValues={initialValues}
      />
    </BORPage>
  );
};

export default CurrentPlanPage;
